import React from 'react';

import { DateRangeSchema } from 'common/hooks/customSearchParams';
import { ButtonDropdown } from 'components/tailwind-ui/ButtonDropdown';
import moment from 'moment';
import { HistoricalPerDiemCalendarPeriod } from '../params';

// This function converts a string to a moment object, or null if the input is invalid.
// The input can be a date string (YYYY-MM-DD) or a string with a value and unit (e.g. "7:days").
// The values "today" and "yesterday" are also supported.
const convertStringToMoment = (input: string): moment.Moment | null => {
  if (!input) return null;

  if (input === 'today') return moment();
  if (input === 'yesterday') return moment().subtract(1, 'days');

  const [value, unit] = input.split(':');

  // If the input is a date string without a unit, return moment(value)
  if (!unit && moment(value, 'YYYY-MM-DD', true).isValid()) {
    return moment(value);
  }

  // If the value is "today" without a unit, return the current moment
  if (value === 'today') return moment();

  const numericValue = parseInt(value, 10);
  if (Number.isNaN(numericValue)) return null; // Return null for invalid input

  return moment().add(numericValue, unit as moment.unitOfTime.DurationConstructor);
};

export type CalendarPeriodFilterFunction = (
  parsedDates: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
    raw: DateRangeSchema;
  },
  period: HistoricalPerDiemCalendarPeriod
) => boolean;

interface Props {
  onChange: (calendarPeriod: HistoricalPerDiemCalendarPeriod) => void;
  dateRange: DateRangeSchema;
  calendarPeriod: HistoricalPerDiemCalendarPeriod;
  filterFunction?: CalendarPeriodFilterFunction;
}

const ALL_PERIODS: HistoricalPerDiemCalendarPeriod[] = [
  HistoricalPerDiemCalendarPeriod.Day,
  HistoricalPerDiemCalendarPeriod.Week,
  HistoricalPerDiemCalendarPeriod.Month,
  HistoricalPerDiemCalendarPeriod.Quarter,
];

export const CalendarPeriodFilter: React.FC<Props> = (props) => {
  const { onChange, dateRange, calendarPeriod, filterFunction } = props;

  const results = React.useMemo(() => {
    const calendarPeriodItems = {
      [HistoricalPerDiemCalendarPeriod.Day]: {
        label: 'By day',
        onClick: () => onChange(HistoricalPerDiemCalendarPeriod.Day),
        active: calendarPeriod === HistoricalPerDiemCalendarPeriod.Day,
        period: HistoricalPerDiemCalendarPeriod.Day,
      },
      [HistoricalPerDiemCalendarPeriod.Week]: {
        label: 'By week',
        onClick: () => onChange(HistoricalPerDiemCalendarPeriod.Week),
        active: calendarPeriod === HistoricalPerDiemCalendarPeriod.Week,
        period: HistoricalPerDiemCalendarPeriod.Week,
      },
      [HistoricalPerDiemCalendarPeriod.Month]: {
        label: 'By month',
        onClick: () => onChange(HistoricalPerDiemCalendarPeriod.Month),
        active: calendarPeriod === HistoricalPerDiemCalendarPeriod.Month,
        period: HistoricalPerDiemCalendarPeriod.Month,
      },
      [HistoricalPerDiemCalendarPeriod.Quarter]: {
        label: 'By quarter',
        onClick: () => onChange(HistoricalPerDiemCalendarPeriod.Quarter),
        active: calendarPeriod === HistoricalPerDiemCalendarPeriod.Quarter,
        period: HistoricalPerDiemCalendarPeriod.Quarter,
      },
    };

    const parsedDates = {
      startDate: moment.isMoment(dateRange.startDate)
        ? dateRange.startDate
        : convertStringToMoment(dateRange?.startDate || ''),
      endDate: moment.isMoment(dateRange.endDate)
        ? dateRange.endDate
        : convertStringToMoment(dateRange?.endDate || ''),
      raw: dateRange,
    };

    const items = ALL_PERIODS.filter((period) =>
      filterFunction ? filterFunction(parsedDates, period) : true
    ).map((period) => calendarPeriodItems[period]);

    const { label, period } = calendarPeriodItems[calendarPeriod] || items[0];

    return {
      items,
      label,
      period,
    };
  }, [dateRange, filterFunction, calendarPeriod, onChange]);

  const { items, label, period } = results;

  React.useEffect(() => {
    onChange(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  return <ButtonDropdown label={label} items={items} />;
};
